import { action, computed, makeObservable, observable } from 'mobx';
import { MultiBulkActionSubItem } from '../MultiBulkActionButton/MultiBulkActionProps';
import { ExtensionMenuItemsState } from '../../state/ExtensionMenuItemsState';
import { MoreActionsItem } from '../MoreActions';
import { CollectionState, FiltersMap } from '@wix/bex-core';
import { resolvePrimaryAndSecondaryActionsCompat } from './resolvePrimaryAndSecondaryActionsCompat';

export interface MultiBulkActionToolbarStateParams<T, F extends FiltersMap> {
  collection: CollectionState<T, F>;
  extensionMenuItems?: ExtensionMenuItemsState;
}

export class MultiBulkActionToolbarState<T, F extends FiltersMap> {
  _primaryActionItems: MultiBulkActionSubItem[] = [];
  _secondaryActionItems: MultiBulkActionSubItem[] = [];

  _extensionMenuItems?: ExtensionMenuItemsState;
  _collection: CollectionState<T, F>;

  constructor(props: MultiBulkActionToolbarStateParams<T, F>) {
    const { collection, extensionMenuItems } = props;

    this._collection = collection;
    this._extensionMenuItems = extensionMenuItems;

    makeObservable(this, {
      _primaryActionItems: observable.ref,
      _secondaryActionItems: observable.ref,
      setActions: action,
      primaryActionItems: computed,
      secondaryActionItems: computed,
    });
  }

  setActions({
    actionItems = [],
    primaryActionItems = [],
    secondaryActionItems = [],
  }: {
    actionItems?: MultiBulkActionSubItem[];
    primaryActionItems?: MultiBulkActionSubItem[];
    secondaryActionItems?: MultiBulkActionSubItem[];
  }) {
    const resolved = resolvePrimaryAndSecondaryActionsCompat(
      actionItems,
      primaryActionItems,
      secondaryActionItems,
    );

    this._primaryActionItems = resolved.primaryActionItems;
    this._secondaryActionItems = resolved.secondaryActionItems;
  }

  private _moreActionItemClick(item: MoreActionsItem) {
    const {
      result: { total },
      bulkSelect,
    } = this._collection;

    const { selectedIds, uncheckedIds, isSelectAll } = bulkSelect;

    item.onClick({
      selectedIds,
      uncheckedIds,
      isSelectAll,
      total,
    });
  }

  get primaryActionItems() {
    return this._primaryActionItems;
  }

  get secondaryActionItems(): MultiBulkActionSubItem[] {
    const moreActionItems =
      this._extensionMenuItems?.menuItems.map((item) => ({
        ...item,
        onClick: () => this._moreActionItemClick(item),
      })) || [];

    return [...this._secondaryActionItems, ...moreActionItems];
  }
}
